import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, ListChecks, RotateCcw, Ban, Trash2,
} from 'lucide-react';
import { PageHeader, Button } from '../ui-v2';
import { Modal, Input, Badge, Table, EmptyState, Tooltip } from '../ui-v2.5';
import {
  listMotivosBaja, crearMotivoBaja, actualizarMotivoBaja, desactivarMotivoBaja,
  eliminarMotivoBajaPermanente,
} from './api';
import EliminarModal from './components/EliminarModal';
import IncorporacionesTabs from './IncorporacionesTabs';

/**
 * Motivos de baja — catálogo administrable, en la misma barra que Áreas, Recursos y Puestos.
 *
 * ── POR QUE EXISTE ────────────────────────────────────────────────────────────────────────────
 *
 * `incorp_bajas.motivo` era texto libre, y con SEIS bajas en producción ya se había bifurcado:
 * «Voluntaria»×3 y «Baja voluntaria»×1 son el mismo motivo escrito de dos formas. Cada baja nueva
 * añadía una redacción más, y después no había forma de contar nada por motivo.
 *
 * ── DESACTIVAR SI, BORRAR SOLO SI NADIE LO USA ────────────────────────────────────────────────
 *
 * Un motivo que ya lleva una baja no se borra: borrarlo sería reescribir la historia de esa baja.
 * Se desactiva —deja de salir en el selector y las bajas que lo llevan lo conservan— y eso se
 * puede hacer siempre.
 *
 * Y el recuento de uso **viaja con la lista**, así que se ve en la tabla antes de pulsar nada. Un
 * «no se puede» que llega después de intentarlo es peor que un número que estaba a la vista.
 */

const cuerpoError = (e) => (e && e.response && e.response.data) || {};

export default function MotivosBaja() {
  const [motivos, setMotivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null);
  const [editor, setEditor] = useState(null);     // { motivo|null }
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      const d = await listMotivosBaja();
      setMotivos(d?.motivos || []);
    } catch (e) {
      console.error('[incorp][motivos] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function cambiarActivo(m, activo) {
    setAccionId(m.id);
    try {
      if (activo) await actualizarMotivoBaja(m.id, { nombre: m.nombre, orden: m.orden, activo: true });
      else await desactivarMotivoBaja(m.id);
      toast.success(`Motivo «${m.nombre}» ${activo ? 'reactivado' : 'desactivado'}.`);
      await cargar();
    } catch (e) {
      console.error('[incorp][motivos] cambiarActivo', e);
      toast.error(cuerpoError(e).message || 'No se pudo cambiar el estado del motivo.');
    } finally {
      setAccionId(null);
    }
  }

  const columns = [
    {
      key: 'nombre',
      header: 'Motivo',
      render: (m) => (
        <div className="flex items-center gap-2">
          <span className={`font-medium ${m.activo ? 'text-v25-ink-900' : 'text-v25-ink-400'}`}>{m.nombre}</span>
          {!m.activo && <Badge tone="expired">Desactivado</Badge>}
        </div>
      ),
    },
    {
      key: 'usos',
      header: 'Bajas que lo usan',
      width: 170,
      /* EL NUMERO ES LA INFORMACION, y por eso está en la tabla y no solo en el rechazo: con 0 se
       * puede borrar, con cualquier otra cosa no, y eso se ve sin tener que intentarlo. */
      render: (m) => (m.usos > 0
        ? <span className="text-v25-ink-700">{m.usos}</span>
        : <span className="text-v25-ink-400">—</span>),
    },
    {
      key: 'acciones',
      header: '',
      width: 190,
      render: (m) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditor({ motivo: m })}><Pencil />Editar</Button>
          {m.activo ? (
            <Tooltip content="Desactivar: deja de salir en el selector y las bajas que lo llevan lo conservan">
              <Button variant="ghost" size="icon" disabled={accionId === m.id} onClick={() => cambiarActivo(m, false)}>
                {accionId === m.id ? <Loader2 className="animate-spin" /> : <Ban />}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip content="Reactivar">
              <Button variant="ghost" size="icon" disabled={accionId === m.id} onClick={() => cambiarActivo(m, true)}>
                {accionId === m.id ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              </Button>
            </Tooltip>
          )}
          <span className="mx-0.5 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
          {/* CON USOS, EL BOTON DE BORRAR SALE DESHABILITADO Y EL `title` DICE POR QUE Y CUANTAS.
              Un botón en gris sin explicación es una pared; con el número, es una respuesta. */}
          <Tooltip content={m.usos > 0
            ? `No se puede eliminar: lo usan ${m.usos} baja(s). Desactívalo en su lugar.`
            : 'Eliminar permanentemente'}>
            <span>
              <Button
                variant="ghost"
                size="icon"
                disabled={m.usos > 0}
                onClick={() => setAEliminar(m)}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 />
              </Button>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Motivos de baja"
        subtitle="El catálogo del que se elige al cerrar una baja. Sustituye al texto libre."
        action={<Button onClick={() => setEditor({ motivo: null })}><Plus />Nuevo motivo</Button>}
      />

      <IncorporacionesTabs active="motivos" />

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando motivos…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar los motivos.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : motivos.length === 0 ? (
        <EmptyState
          variant="medium"
          icon={ListChecks}
          title="Aún no hay motivos"
          description="Crea el primero: será una de las opciones al cerrar una baja."
          action={<Button onClick={() => setEditor({ motivo: null })}><Plus />Nuevo motivo</Button>}
        />
      ) : (
        <Table columns={columns} rows={motivos} />
      )}

      {editor && (
        <MotivoModal
          motivo={editor.motivo}
          onClose={() => setEditor(null)}
          onGuardado={() => { setEditor(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar motivo"
          subtitulo={aEliminar.nombre}
          exitoMsg={`Motivo «${aEliminar.nombre}» eliminado permanentemente.`}
          eliminar={() => eliminarMotivoBajaPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará el motivo <strong>«{aEliminar.nombre}»</strong> del catálogo de forma
          permanente. Esta acción no se puede deshacer.
        </EliminarModal>
      )}
    </div>
  );
}

// ─────────────────────────── Modal editor (crear/editar) ───────────────────────────
function MotivoModal({ motivo, onClose, onGuardado }) {
  const editando = !!motivo;
  const [nombre, setNombre] = useState(motivo?.nombre || '');
  const [orden, setOrden] = useState(motivo?.orden != null ? String(motivo.orden) : '');
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [reactivable, setReactivable] = useState(null); // del 409 de duplicado desactivado

  function validar() {
    const e = {};
    if (!nombre.trim()) e.nombre = 'El nombre es obligatorio.';
    else if (nombre.trim().length > 60) e.nombre = 'Máximo 60 caracteres.';
    if (orden !== '' && (!/^\d+$/.test(orden))) e.orden = 'El orden debe ser un entero ≥ 0.';
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  async function guardar() {
    if (!validar()) return;
    setGuardando(true);
    setReactivable(null);
    try {
      const payload = { nombre: nombre.trim(), orden: orden === '' ? 0 : Number(orden) };
      if (editando) await actualizarMotivoBaja(motivo.id, payload);
      else await crearMotivoBaja(payload);
      toast.success(`Motivo «${payload.nombre}» ${editando ? 'actualizado' : 'creado'}.`);
      onGuardado();
    } catch (e) {
      const d = cuerpoError(e);
      if (d.error === 'nombre_duplicado_inactivo') {
        setReactivable({ id: d.id, mensaje: d.message });
      } else if (d.error === 'nombre_duplicado') {
        /* El mensaje del servidor explica que no se distinguen mayúsculas ni acentos. Se enseña
         * TAL CUAL: sin esa frase, rechazar «Despido Objetivo» junto a «Despido objetivo» parece
         * un fallo del sistema y no una regla. */
        setErrores({ nombre: d.message });
      } else {
        toast.error(d.message || 'No se pudo guardar el motivo.');
      }
    } finally {
      setGuardando(false);
    }
  }

  async function reactivarDuplicado() {
    setGuardando(true);
    try {
      await actualizarMotivoBaja(reactivable.id, { nombre: nombre.trim(), orden: orden === '' ? 0 : Number(orden), activo: true });
      toast.success(`Motivo «${nombre.trim()}» reactivado.`);
      onGuardado();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el motivo.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editando ? 'Editar motivo' : 'Nuevo motivo'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={guardando}>Cancelar</Button>
          {reactivable ? (
            <Button onClick={reactivarDuplicado} disabled={guardando}>
              {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar el existente
            </Button>
          ) : (
            <Button onClick={guardar} disabled={guardando}>
              {guardando ? <Loader2 className="animate-spin" /> : null}{editando ? 'Guardar' : 'Crear'}
            </Button>
          )}
        </>
      )}
    >
      <div className="space-y-3">
        <Input
          label="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          error={errores.nombre}
          placeholder="Fin de contrato"
        />
        <Input
          label="Orden"
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          error={errores.orden}
          hint="Posición en el desplegable. Menor, antes."
          placeholder="10"
        />
        {reactivable && (
          <p className="text-sm text-amber-700">{reactivable.mensaje}</p>
        )}
        {editando && motivo.usos > 0 && (
          /* Renombrar con usos SÍ se permite: corregir una errata deja las bajas apuntando al
           * mismo motivo —el id no cambia—. Se dice para que nadie tema tocarlo. */
          <p className="text-sm text-v25-ink-500">
            Lo usan <strong>{motivo.usos}</strong> baja(s). Cambiar el nombre no las desvincula:
            siguen apuntando a este motivo.
          </p>
        )}
      </div>
    </Modal>
  );
}
